import {
  CloseButton,
  P2GBox,
  P2GDrawer,
  P2GGrid,
  P2GList,
  P2GListItem,
  P2GText,
  useP2GMediaQueryOnly
} from '@parcel2go/core-ui';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '~/components/core/progress/loading';
import { TranslationNamespaces } from '~/lib/constants';
import { GenericErrorResponse } from '~/lib/types/basket-types';

const PaymentCore = lazy(() => import('~/components/payments/core'));

type props = {
  open: boolean;
  onClose: () => void;
  creationErrors: GenericErrorResponse | undefined;
  order?: { orderId: string; hash: string } | null;
};

export function BasketProcessing({ onClose, open, creationErrors, order }: props) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const isXs = useP2GMediaQueryOnly('xs');
  return (
    <P2GDrawer
      open={open}
      anchor={isXs ? 'bottom' : 'right'}
      onClose={order ? onClose : undefined}
      SlideProps={{ style: { width: isXs ? undefined : '550px' } }}
    >
      <P2GBox p={2}>
        {creationErrors && (
          <P2GGrid direction="column" spacing={2}>
            <P2GGrid item>
              <CloseButton closeButtonText={'Close'} handleOnClose={onClose} />
            </P2GGrid>
            <P2GGrid item>
              <P2GText variant="h2" align="center" component="p">
                {t('BASKET_CREATION_ERROR', { defaultValue: 'Error creating your order' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <P2GText align="center" component="p">
                {creationErrors.title}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <P2GText align="center" component="p">
                {creationErrors.detail}
              </P2GText>
            </P2GGrid>
            {creationErrors.issues && (
              <P2GGrid item>
                <P2GList>
                  {creationErrors.issues.map((error, index) => (
                    <P2GListItem key={index} text={error.detail} />
                  ))}
                </P2GList>
              </P2GGrid>
            )}
          </P2GGrid>
        )}
        {!creationErrors && !order && (
          <P2GGrid direction="column" spacing={2}>
            <P2GGrid item>
              <P2GText variant="h2" align="center" component="p" dataTestId="basket-creation-loader">
                {t('BASKET_CREATION_LOADER_TITLE', { defaultValue: 'Creating your order' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <Loading />
            </P2GGrid>
          </P2GGrid>
        )}
        {order && (
          <P2GGrid direction="column" spacing={2}>
            <P2GGrid item>
              <CloseButton
                closeButtonText={t('BASKET_PAYMENT_CLOSE', { defaultValue: 'Close' })}
                handleOnClose={onClose}
              />
            </P2GGrid>
            <P2GGrid item>
              <P2GText variant="h2" component="p" dataTestId="basket-payment-title" mb={4}>
                {t('BASKET_PAYMENT_TITLE', { defaultValue: 'Payment' })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <Suspense fallback={<Loading />}>
                <PaymentCore id={order.orderId} hash={order.hash} />
              </Suspense>
            </P2GGrid>
          </P2GGrid>
        )}
      </P2GBox>
    </P2GDrawer>
  );
}
